import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  Check as ApproveIcon,
  Check,
  Close as RejectIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { UserContext } from "../context/UserContext.js";
import OpexService from "../service/opexService.js";
import Q8NoData from "../components/Q8Nodata.js";

const OpexPendingChanges = ({onlyAllocation, title, href, secondHref}) => {
  // State
  const [pendingChanges, setPendingChanges] = useState([]);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });
  const [loading, _] = useState(true);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
    change: null,
    title: "",
    message: "",
  });
  // NUOVO: Stato per l'opzione di blocco
  const [lockAfterApproval, setLockAfterApproval] = useState(false);

  const { user } = useContext(UserContext);

  // Helper functions
  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  const toggleExpandRecord = (id) => {
    setExpandedRecords((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const loadPendingChanges = async () => {
    try {
      const response = await OpexService.getPendingRecordChanges(onlyAllocation);

      // Parse JSON strings to objects
      const processedChanges = response.map((change) => ({
        ...change,
        originalRecord: JSON.parse(change.originalRecordJson),
        modifiedRecord: JSON.parse(change.modifiedRecordJson),
      }));

      setPendingChanges(processedChanges);
    } catch (error) {
      console.error("Error loading pending changes:", error);
      showNotification("Error loading pending changes", "error");
    }
  };

  // Load data when component mounts
  useEffect(() => {
    loadPendingChanges();
  }, []);

  const openConfirmDialog = (action, change) => {
    const isApprove = action === "approve";
    const identifier = change.contract || change.opexOriginalId; // Usa opexOriginalId se contract è null

    setConfirmDialog({
      open: true,
      action,
      change,
      title: isApprove ? "Approva Modifica" : "Rifiuta Modifica",
      message: isApprove
        ? `Sei sicuro di voler approvare tutte le modifiche a ${title} ${identifier}?`
        : `Sei sicuro di voler rifiutare tutte le modifiche a ${title} ${identifier}?`,
    });

    // Reset lock option when opening dialog
    setLockAfterApproval(false);
  };

  const openApproveAllDialog = () => {
    const pendingCount = pendingChanges.filter(
      (change) => change.status === "pending"
    ).length;

    setConfirmDialog({
      open: true,
      action: "approveAll",
      change: null,
      title: "Approva tutte le modifiche",
      message: `Sei sicuro di voler approvare le ${pendingCount} modifiche pendenti?`,
    });

    // Reset lock option when opening dialog
    setLockAfterApproval(false);
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
    });
  };

  const handleConfirmAction = async () => {
    const { action, change } = confirmDialog;
    closeConfirmDialog();

    try {
      if (action === "approveAll") {
        // Get only changes with "pending" status
        const pendingItems = pendingChanges.filter(
          (item) => item.status === "pending"
        );

        if (pendingItems.length === 0) {
          showNotification("Nessuna modifica pendente da approvare", "warning");
          return;
        }

        // Set loading state
        setBulkActionLoading(true);

        // Show loading message
        showNotification("Approvando tutte le modifiche...", "info");

        // Approve each change sequentially
        let approvedCount = 0;
        for (const item of pendingItems) {
          const payload = {
            changeId: item.id,
            handledBy: user.email,
            notes: "Bulk approval",
            lockRecord: lockAfterApproval, // NUOVO: Aggiungi il flag di blocco
          };

          await OpexService.approveRecordPendingChange(item.id, payload);
          approvedCount++;
        }

        showNotification(
          `${approvedCount} modifiche approvate con successo` +
            (lockAfterApproval
              ? " e records bloccati per le modifiche future"
              : ""),
          "success"
        );
      } else if (action === "approve") {
        await OpexService.approveRecordPendingChange(change.id, {
          changeId: change.id,
          handledBy: user.email,
          notes: lockAfterApproval
            ? "Approvate e bloccate per cambiamenti futuri"
            : "",
          lockRecord: lockAfterApproval, // NUOVO: Aggiungi il flag di blocco
        });

        const identifier = change.contract || change.opexOriginalId;
        showNotification(
          `Modifiche a ${title} ${identifier} approvate con successo` +
            (lockAfterApproval
              ? " e records bloccati per le modifiche future"
              : ""),
          "success"
        );
      } else {
        await OpexService.rejectRecordPendingChange(change.id, {
          changeId: change.id,
          handledBy: user.email,
          notes: "",
        });

        const identifier = change.contract || change.opexOriginalId;
        showNotification(`Modifiche a ${title} ${identifier} rifiutate`, "info");
      }

      // Reload data
      await loadPendingChanges();
    } catch (error) {
      console.error(
        `Error during ${
          action === "approveAll"
            ? "approva tutte le modifiche"
            : action === "approve"
            ? "approval"
            : "rejection"
        }:`,
        error
      );
      showNotification(
        `Error during ${
          action === "approveAll"
            ? "approvazione di tutte le modifiche"
            : action === "approve"
            ? "approval"
            : "rejection"
        }`,
        "error"
      );
    }
  };

  // Function to detect and highlight differences between original and modified records
  const isDifferent = (key, originalRecord, modifiedRecord) => {
    if (!originalRecord || !modifiedRecord) return false;

    // For numbers, convert to string for comparison display purposes
    const originalValue =
      typeof originalRecord[key] === "number"
        ? originalRecord[key].toString()
        : originalRecord[key];

    const modifiedValue =
      typeof modifiedRecord[key] === "number"
        ? modifiedRecord[key].toString()
        : modifiedRecord[key];

    return originalValue !== modifiedValue;
  };

  // Function to format values for display
  const formatValue = (key, value) => {
    if (value === null || value === undefined) return "-";

    // Format monetary values
    if (["euro", "euro_SAP"].includes(key) && typeof value === "number") {
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    }

    // Format dates
    if (
      ["dataDocumento", "dataRegistrazione"].includes(key) &&
      value instanceof Date
    ) {
      return value.toLocaleDateString();
    }

    return value.toString();
  };

  // Get the keys to display from both original and modified record
  const getKeysToDisplay = (originalRecord, modifiedRecord) => {
    if (!originalRecord && !modifiedRecord) return [];

    // Merge keys from both records so fields added/changed only in modifiedRecord are included
    const combinedKeys = new Set([
      ...Object.keys(originalRecord || {}),
      ...Object.keys(modifiedRecord || {}),
    ]);

    // Define keys to display in the order you want
    const orderedKeys = [
      "contract",
      "flusso",
      "centroDiCosto",
      "descCdc",
      "voceDiCosto",
      "descConto",
      "creatoreDocumentoCO",
      "contoDiContropartita",
      "desConto",
      "coDocumentNumber",
      "posizioneDocumentoCO",
      "documentoDiRiferimento",
      "posizioneDocumentoDiRiferimento",
      "periodo",
      "esercizio",
      "contabilitSecond",
      "fornitore",
      "descFornitore",
      "cliente",
      "descCliente",
      "oda",
      "posizioneOda",
      "descOda",
      "buyer",
      "collaudatoDa",
      "dataDocumento",
      "dataRegistrazione",
      "testoDocumentoCO",
      "descrizioneDocCO",
      "euro",
      "quantita",
      "corporate",
      "sap_OU",
      "raggruppamento1",
      "raggruppamento2",
      "raggruppamento3",
      "responsabile",
    ];

    // Filter out keys that don't exist in either record
    return orderedKeys.filter((key) => combinedKeys.has(key));
  };

  // Function to get a more user-friendly field name
  const getFieldDisplayName = (key) => {
    const fieldNameMap = {
      contract: "Contract",
      flusso: "Flusso",
      centroDiCosto: "CDC",
      descCdc: "Desc CDC",
      voceDiCosto: "Conto",
      descConto: "Desc Conto",
      creatoreDocumentoCO: "Creatore Documento CO",
      contoDiContropartita: "Conto di Contropartita",
      desConto: "Des Conto Contropartita",
      coDocumentNumber: "CO Document Number",
      posizioneDocumentoCO: "Posizione Documento CO",
      documentoDiRiferimento: "Documento di Riferimento",
      posizioneDocumentoDiRiferimento: "Posizione Doc Riferimento",
      periodo: "Mese Fiscale",
      esercizio: "Esercizio",
      contabilitSecond: "Contabilit Second",
      fornitore: "Fornitore",
      descFornitore: "Desc Fornitore",
      cliente: "Cliente",
      descCliente: "Desc Cliente",
      oda: "Oda",
      posizioneOda: "Posizione Oda",
      descOda: "Desc Oda",
      buyer: "Buyer",
      collaudatoDa: "Collaudato Da",
      dataDocumento: "Data Documento",
      dataRegistrazione: "Data Registrazione",
      testoDocumentoCO: "Testo Documento CO",
      descrizioneDocCO: "Descrizione Doc CO",
      euro: "Euro",
      quantita: "Quantità",
      corporate: "Local/Corporate",
      sap_OU: "SAP OU",
      raggruppamento1: "Liv 1",
      raggruppamento2: "Liv 2",
      raggruppamento3: "Liv 3",
      responsabile: "Responsabile",
    };

    return fieldNameMap[key] || key;
  };

  return (
    <Box
      sx={{ height: "100vh", display: "flex", flexDirection: "column", p: 2 }}
    >
      <Q8Breadcrumbs
        links={[
          { label: title, href: href },
          { label: "Modifiche pendenti", href: secondHref },
        ]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: "primary.main" }}>
            {title} Modifiche Pendenti
          </Typography>

          {pendingChanges.filter((change) => change.status === "pending")
            .length > 0 && (
            <Q8Button
              variant="primary"
              label={
                bulkActionLoading ? "Approvazione in corso..." : "Approva Tutti"
              }
              onClick={openApproveAllDialog}
              size="medium"
              startIcon={<Check />}
            />
          )}
        </Box>

        <Typography variant="body1" sx={{ mt: 1 }}>
          Rivedi ed approva o rifiuta le modifiche proposte agli {title} records.
        </Typography>
      </Box>

   
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Identificativo</TableCell>
                <TableCell>Modificato da</TableCell>
                <TableCell>Modificato il</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingChanges.map((change) => (
                <React.Fragment key={change.id}>
                  <TableRow>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpandRecord(change.id)}
                      >
                        {expandedRecords[change.id] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {change.contract || change.opexOriginalId}
                    </TableCell>
                    <TableCell>{change.modifiedBy}</TableCell>
                    <TableCell>
                      {new Date(change.modifiedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          change.status === "pending"
                            ? "In attesa"
                            : change.status === "approved"
                            ? "Approvato"
                            : ""
                        }
                        color={
                          change.status === "pending"
                            ? "warning"
                            : change.status === "approved"
                            ? "success"
                            : "error"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {change.status === "pending" && (
                        <>
                          <Tooltip title="Approva Modifiche">
                            <IconButton
                              color="success"
                              onClick={() =>
                                openConfirmDialog("approve", change)
                              }
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rifiuta modifiche">
                            <IconButton
                              color="error"
                              onClick={() =>
                                openConfirmDialog("reject", change)
                              }
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={6}
                    >
                      <Collapse
                        in={expandedRecords[change.id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ m: 2 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            Change Details
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                                  }}
                                >
                                  Field
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                                  }}
                                >
                                  Original Value
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                                  }}
                                >
                                  Modified Value
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {getKeysToDisplay(change.originalRecord, change.modifiedRecord).map(
                                (key) => {
                                  const isDiff = isDifferent(
                                    key,
                                    change.originalRecord,
                                    change.modifiedRecord
                                  );
                                  return (
                                    <TableRow
                                      key={key}
                                      sx={{
                                        backgroundColor: isDiff
                                          ? "rgba(255, 243, 224, 0.5)"
                                          : "inherit",
                                      }}
                                    >
                                      <TableCell>
                                        {getFieldDisplayName(key)}
                                      </TableCell>
                                      <TableCell>
                                        {formatValue(
                                          key,
                                          change.originalRecord[key]
                                        )}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: isDiff
                                            ? "bold"
                                            : "normal",
                                          color: isDiff
                                            ? "primary.main"
                                            : "inherit",
                                        }}
                                      >
                                        {formatValue(
                                          key,
                                          change.modifiedRecord[key]
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                              )}
                              {getKeysToDisplay(change.originalRecord, change.modifiedRecord)
                                .length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={3} align="center">
                                    No differences found or incomplete data
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          {pendingChanges.length === 0 && <Q8NoData />}
        </TableContainer>
      {/* Confirmation Dialog - MODIFICATO per includere l'opzione di blocco */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>

          {/* Opzione per bloccare il record */}
          {/* {(confirmDialog.action === "approve" ||
            confirmDialog.action === "approveAll") && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={lockAfterApproval}
                  onChange={(e) => setLockAfterApproval(e.target.checked)}
                  icon={<LockIcon />}
                  checkedIcon={<LockIcon />}
                />
              }
              label="Blocca questo record per modifiche future"
              sx={{ mt: 2, display: "flex", alignItems: "center" }}
            />
          )} */}
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={closeConfirmDialog}
          />
          <Q8Button
            variant="primary"
            label="Conferma"
            onClick={handleConfirmAction}
          />
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        title="Notification"
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default OpexPendingChanges;
